import {
  Edge,
  GraphStructure,
  Node,
  SplitBranch,
  SplitData,
  SplitGoal,
  SplitWinnerSelection,
} from "../types";
import { createNewEdge, createNewNode } from "../Helpers/commons";

export const SPLIT_NODE_LABEL = "Traffic Split";
export const MIN_SPLIT_BRANCHES = 2;
export const MAX_SPLIT_BRANCHES = 5;
export const MAX_SPLIT_BRANCH_ID_LENGTH = 64;
export const MIN_SPLIT_PERCENTAGE = 1;
export const MAX_SPLIT_PERCENTAGE = 99;
export const MIN_SPLIT_TEST_HOURS = 1;
export const MAX_SPLIT_TEST_HOURS = 720;

export const SPLIT_GOAL_OPTIONS: { value: SplitGoal; label: string }[] = [
  { value: "OPEN_RATE", label: "Open rate" },
  { value: "CLICK_RATE", label: "Click rate" },
  { value: "CONVERSION_RATE", label: "Conversion rate" },
  { value: "REVENUE_PER_RECIPIENT", label: "Revenue per recipient" },
];

export const SPLIT_WINNER_SELECTION_OPTIONS: {
  value: SplitWinnerSelection;
  label: string;
  description: string;
}[] = [
  {
    value: "AUTOMATIC",
    label: "Automatic",
    description: "Pick the winner automatically after the test period",
  },
  {
    value: "MANUAL",
    label: "Manual",
    description: "I'll pick the winner myself",
  },
  {
    value: "LIFETIME",
    label: "Lifetime split",
    description: "Keep splitting forever, never pick a winner",
  },
];

export const LIFETIME_SPLIT_HELPER_TEXT =
  "Contacts will keep being split by these percentages. No winner will be picked.";

export const LIFETIME_SWITCH_CONFIRMATION =
  "Switching to a lifetime split removes the current winner and restarts the stats.";

/** A lifetime split never has a winner, so its goal is optional and only tracked. */
export const picksWinner = (winnerSelection?: SplitWinnerSelection | null) =>
  winnerSelection !== "LIFETIME";

/** The node card's mode line: "Auto winner · 72h", "Manual winner" or "Lifetime split". */
export const getSplitModeLabel = (data: SplitData) => {
  if (data.winnerSelection === "LIFETIME") return "Lifetime split";
  if (data.winnerSelection === "AUTOMATIC") {
    return data.testDurationHours != null
      ? `Auto winner · ${data.testDurationHours}h`
      : "Auto winner";
  }
  return "Manual winner";
};

export const getSplitGoalLabel = (goal?: SplitGoal | null) =>
  SPLIT_GOAL_OPTIONS.find((option) => option.value === goal)?.label || "";

/** Conversion rate and revenue come from store orders, so they need Shopify. */
export const isShopifyGoal = (goal?: SplitGoal | null) =>
  goal === "CONVERSION_RATE" || goal === "REVENUE_PER_RECIPIENT";

/**
 * The goals an account can pick. A Shopify-only goal the node already has
 * stays listed, so an existing split still shows and saves its goal.
 */
export const getSplitGoalOptions = (
  shopifyIntegration: boolean,
  currentGoal?: SplitGoal | ""
) =>
  SPLIT_GOAL_OPTIONS.filter(
    (option) =>
      shopifyIntegration ||
      !isShopifyGoal(option.value) ||
      option.value === currentGoal
  );

/** Open and click rate are measured on the emails each branch sends before the branches rejoin. */
export const isEmailGoal = (goal?: SplitGoal | null) =>
  goal === "OPEN_RATE" || goal === "CLICK_RATE";

/**
 * Bijective base-26 ids: a..z, aa..az, ba... Index 0 is "a".
 */
const branchIdFromIndex = (index: number): string => {
  let id = "";
  let n = index + 1;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    id = String.fromCharCode(97 + remainder) + id;
    n = Math.floor((n - 1) / 26);
  }
  return id;
};

const branchIndexFromId = (id: string): number | undefined => {
  if (!/^[a-z]+$/.test(id)) {
    return undefined;
  }
  let n = 0;
  for (const char of id) {
    n = n * 26 + (char.charCodeAt(0) - 96);
  }
  return n - 1;
};

/**
 * The next letter after every id already on the node. A removed id is never
 * handed out again: re-using it would let a brand-new branch inherit the old
 * branch's assignments without the backend restarting the test.
 */
export const nextBranchId = (branches: SplitBranch[]): string => {
  const highest = branches.reduce((max, branch) => {
    const index = branchIndexFromId(branch.id);
    return index !== undefined && index > max ? index : max;
  }, -1);
  const used = new Set(branches.map((branch) => branch.id));
  let index = highest + 1;
  while (used.has(branchIdFromIndex(index))) {
    index++;
  }
  return branchIdFromIndex(index);
};

export const createDefaultSplitData = (): SplitData => ({
  branches: [
    { id: "a", name: "Path A", percentage: 50 },
    { id: "b", name: "Path B", percentage: 50 },
  ],
  winnerSelection: "MANUAL",
});

export const getSplitTotal = (branches: SplitBranch[] = []) =>
  branches.reduce(
    (total, branch) => total + (Number(branch.percentage) || 0),
    0
  );

export const isValidSplitPercentage = (value: unknown) =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= MIN_SPLIT_PERCENTAGE &&
  value <= MAX_SPLIT_PERCENTAGE;

export const isValidSplitTestDuration = (value: unknown) =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= MIN_SPLIT_TEST_HOURS &&
  value <= MAX_SPLIT_TEST_HOURS;

export const getBranchHandleLabel = (branch: SplitBranch) =>
  `${branch.name} · ${branch.percentage}%`;

/** e.g. "60 / 40 · Open rate". */
export const getSplitSummary = (data: SplitData) => {
  const percentages = (data.branches || [])
    .map((branch) => branch.percentage)
    .join(" / ");
  const goal = getSplitGoalLabel(data.goal);
  return goal ? `${percentages} · ${goal}` : percentages;
};

export const getUnconnectedBranches = (
  nodeId: string,
  branches: SplitBranch[] = [],
  edges: Edge[]
) =>
  branches.filter(
    (branch) =>
      !edges.some(
        (edge) => edge.source === nodeId && edge.sourceHandle === branch.id
      )
  );

/**
 * Everything that blocks saving the split's settings. Messages are shown as-is.
 */
export const validateSplitSettings = (data: SplitData): string[] => {
  const errors: string[] = [];
  const branches = data.branches || [];

  if (
    branches.length < MIN_SPLIT_BRANCHES ||
    branches.length > MAX_SPLIT_BRANCHES
  ) {
    errors.push(
      `A split needs ${MIN_SPLIT_BRANCHES} to ${MAX_SPLIT_BRANCHES} paths.`
    );
  }
  if (branches.some((branch) => !branch.name?.trim())) {
    errors.push("Every path needs a name.");
  }
  const ids = branches.map((branch) => branch.id);
  if (
    ids.some((id) => !id || id.length > MAX_SPLIT_BRANCH_ID_LENGTH) ||
    new Set(ids).size !== ids.length
  ) {
    errors.push("Every path needs its own id.");
  }
  if (branches.some((branch) => !isValidSplitPercentage(branch.percentage))) {
    errors.push(
      `Each percentage must be a whole number from ${MIN_SPLIT_PERCENTAGE} to ${MAX_SPLIT_PERCENTAGE}.`
    );
  }
  const total = getSplitTotal(branches);
  if (total !== 100) {
    errors.push(`Percentages must add up to 100% (now ${total}%).`);
  }
  if (!data.goal && picksWinner(data.winnerSelection)) {
    errors.push("Choose a goal.");
  }
  if (
    data.winnerSelection === "AUTOMATIC" &&
    !isValidSplitTestDuration(data.testDurationHours)
  ) {
    errors.push(
      `Test duration must be a whole number of hours from ${MIN_SPLIT_TEST_HOURS} to ${MAX_SPLIT_TEST_HOURS}.`
    );
  }
  return errors;
};

/** Settings errors plus the canvas check that every branch has its edge. */
export const validateSplitNode = (node: Node, edges: Edge[]): string[] => [
  ...validateSplitSettings(node.data),
  ...getUnconnectedBranches(node.id, node.data.branches, edges).map(
    (branch) => `Path '${branch.name}' has no connection.`
  ),
];

/**
 * The split's settings exactly as the backend expects them in
 * `structure.nodes[].data`. testDurationHours is left out entirely when the
 * winner is picked by hand.
 */
export const buildSplitData = (values: {
  branches: SplitBranch[];
  goal?: SplitGoal;
  winnerSelection: SplitWinnerSelection;
  testDurationHours?: number | null;
}): SplitData => ({
  branches: values.branches.map((branch) => ({
    id: branch.id,
    name: branch.name.trim(),
    percentage: branch.percentage,
  })),
  // Left out entirely when a lifetime split tracks no metric.
  ...(values.goal && { goal: values.goal }),
  winnerSelection: values.winnerSelection,
  ...(values.winnerSelection === "AUTOMATIC" &&
    values.testDurationHours != null && {
      testDurationHours: values.testDurationHours,
    }),
});

/** Every node and edge below `nodeId`. The builder's graph is a tree. */
export const collectDescendants = (
  nodeId: string,
  edges: Edge[]
): { nodeIds: Set<string>; edgeIds: Set<string> } => {
  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();
  const stack = [nodeId];
  while (stack.length) {
    const current = stack.pop() as string;
    edges
      .filter((edge) => edge.source === current)
      .forEach((edge) => {
        edgeIds.add(edge.id);
        if (!nodeIds.has(edge.target)) {
          nodeIds.add(edge.target);
          stack.push(edge.target);
        }
      });
  }
  return { nodeIds, edgeIds };
};

export const createExitNode = (id: string): Node =>
  createNewNode(
    id,
    "EXIT",
    { x: 0, y: 0 },
    { label: "Exit", config: { configured: true } }
  );

/**
 * Applies a new branch list to the split's outgoing edges, matched by
 * sourceHandle only: a removed branch takes its edge and everything below it
 * along, and a new branch gets an edge to a fresh Exit node.
 */
export const applySplitBranches = (
  structure: GraphStructure,
  nodeId: string,
  branches: SplitBranch[],
  newId: () => string
): GraphStructure => {
  const branchIds = new Set(branches.map((branch) => branch.id));
  const outgoing = structure.edges.filter((edge) => edge.source === nodeId);

  const nodeIdsToRemove = new Set<string>();
  const edgeIdsToRemove = new Set<string>();
  outgoing
    .filter((edge) => !edge.sourceHandle || !branchIds.has(edge.sourceHandle))
    .forEach((edge) => {
      edgeIdsToRemove.add(edge.id);
      nodeIdsToRemove.add(edge.target);
      const below = collectDescendants(edge.target, structure.edges);
      below.nodeIds.forEach((id) => nodeIdsToRemove.add(id));
      below.edgeIds.forEach((id) => edgeIdsToRemove.add(id));
    });

  const nodes = structure.nodes.filter((node) => !nodeIdsToRemove.has(node.id));
  const edges = structure.edges.filter((edge) => !edgeIdsToRemove.has(edge.id));

  branches
    .filter(
      (branch) =>
        !edges.some(
          (edge) => edge.source === nodeId && edge.sourceHandle === branch.id
        )
    )
    .forEach((branch) => {
      const exitNode = createExitNode(newId());
      nodes.push(exitNode);
      edges.push(createNewEdge(nodeId, exitNode.id, branch.id));
    });

  return { nodes, edges };
};

/**
 * Inserts a new split on an edge: the first branch continues to whatever the
 * edge pointed at, every other branch ends in its own Exit.
 */
export const createSplitInsertion = (
  edge: Edge,
  label: string,
  newId: () => string
): { nodes: Node[]; edges: Edge[] } => {
  const data = createDefaultSplitData();
  const splitNode = createNewNode(
    newId(),
    "SPLIT",
    { x: 0, y: 0 },
    { label, ...data, config: { configured: false } }
  );
  const [first, ...rest] = data.branches as SplitBranch[];
  const nodes: Node[] = [splitNode];
  const edges: Edge[] = [
    createNewEdge(edge.source, splitNode.id, edge.sourceHandle || null),
    createNewEdge(splitNode.id, edge.target, first.id),
  ];
  rest.forEach((branch) => {
    const exitNode = createExitNode(newId());
    nodes.push(exitNode);
    edges.push(createNewEdge(splitNode.id, exitNode.id, branch.id));
  });
  return { nodes, edges };
};

/**
 * Removes a split. With `keepBranchId` that branch takes the split's place;
 * without it every branch goes and an Exit takes its place.
 */
export const removeSplit = (
  structure: GraphStructure,
  nodeId: string,
  keepBranchId: string | null,
  newId: () => string
): GraphStructure | undefined => {
  const inputEdge = structure.edges.find((edge) => edge.target === nodeId);
  if (!inputEdge) return undefined;

  const keptEdge = keepBranchId
    ? structure.edges.find(
        (edge) => edge.source === nodeId && edge.sourceHandle === keepBranchId
      )
    : undefined;
  if (keepBranchId && !keptEdge) return undefined;

  const nodeIdsToRemove = new Set<string>();
  const edgeIdsToRemove = new Set<string>();
  structure.edges
    .filter((edge) => edge.source === nodeId)
    .forEach((edge) => {
      edgeIdsToRemove.add(edge.id);
      if (edge === keptEdge) return;
      nodeIdsToRemove.add(edge.target);
      const below = collectDescendants(edge.target, structure.edges);
      below.nodeIds.forEach((id) => nodeIdsToRemove.add(id));
      below.edgeIds.forEach((id) => edgeIdsToRemove.add(id));
    });

  let replacement: Node | undefined;
  let newTarget = keptEdge?.target;
  if (!newTarget) {
    replacement = createExitNode(newId());
    newTarget = replacement.id;
  }

  const nodes: Node[] = [];
  structure.nodes.forEach((node) => {
    if (node.id === nodeId) {
      if (replacement) nodes.push(replacement);
    } else if (!nodeIdsToRemove.has(node.id)) {
      nodes.push(node);
    }
  });
  const edges = structure.edges
    .filter((edge) => !edgeIdsToRemove.has(edge.id))
    .map((edge) =>
      edge.id === inputEdge.id
        ? createNewEdge(edge.source, newTarget as string, edge.sourceHandle)
        : edge
    );

  return { nodes, edges };
};

/**
 * The backend restarts a split's test when its goal or its ordered branch ids
 * change; percentages, duration and mode keep it running.
 */
export const splitRestartsTest = (
  draft: SplitData,
  published: { goal?: SplitGoal | null; branchIds: string[] }
) => {
  const draftIds = (draft.branches || []).map((branch) => branch.id);
  // The backend hashes a missing goal as null, so none and none match.
  return (
    (draft.goal ?? null) !== (published.goal ?? null) ||
    draftIds.length !== published.branchIds.length ||
    draftIds.some((id, index) => id !== published.branchIds[index])
  );
};

/**
 * Publishing a split as LIFETIME while its published test has a winner clears
 * that winner and starts a new round, even when goal and branches are unchanged.
 */
export const splitClearsWinner = (
  draft: SplitData,
  published: { hasWinner: boolean }
) => draft.winnerSelection === "LIFETIME" && published.hasWinner;

export const getLifetimeSwitchWarning = (splitName: string) =>
  `Switching '${splitName}' to a lifetime split removes the current winner and restarts the stats.`;

export const getSplitRestartWarning = (splitName: string) =>
  `Changing the branches or goal of '${splitName}' restarts its A/B test: results so far are reset and any winner is cleared.`;
